import { useMutation } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { updatePrepItemRestDays } from '../gql'

interface IEditRestDaysModalProps {
  visible: boolean
  onClose: () => void
  prepItemId: string
  prepItemName: string
  currentRestDays: number
  onSuccess?: () => void
}

const EditRestDaysModal = ({
  visible,
  onClose,
  prepItemId,
  prepItemName,
  currentRestDays,
  onSuccess,
}: IEditRestDaysModalProps) => {
  const [restDays, setRestDays] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [updateRestDaysMutation] = useMutation(updatePrepItemRestDays)

  useEffect(() => {
    if (visible) {
      setRestDays(currentRestDays.toString())
      setError(null)
      setSuccess(false)
    }
  }, [visible, currentRestDays])

  const handleSave = async () => {
    // Check if the field is empty
    if (!restDays || restDays.trim() === '') {
      setError('Rest Days is required')
      return
    }

    const value = parseInt(restDays, 10)
    if (isNaN(value) || value < 0) {
      setError('Please enter a valid non-negative integer')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await updateRestDaysMutation({
        variables: {
          prepItemId: prepItemId,
          restDays: value,
        },
      })

      setSuccess(true)

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }

      // Close modal after showing success for a moment
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setError(null)
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      setError('Failed to update. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Rest Days</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Prep Item
            </Typography>
            <TextField
              value={prepItemName}
              disabled
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Rest Days
            </Typography>
            <TextField
              value={restDays}
              onChange={(e) => {
                const value = e.target.value
                if (value === '' || /^\d+$/.test(value)) {
                  setRestDays(value)
                }
              }}
              placeholder="Enter non-negative integer"
              fullWidth
              size="small"
            />
            <Typography
              variant="body2"
              sx={{ mt: 1, color: 'rgba(0, 0, 0, 0.6)', fontStyle: 'italic' }}
            >
              Need{' '}
              {restDays && parseInt(restDays, 10) >= 0
                ? restDays
                : currentRestDays}{' '}
              day(s) rest period, so the amount that is prepped on that day
              cannot be used until the next{' '}
              {restDays && parseInt(restDays, 10) >= 0
                ? restDays
                : currentRestDays}{' '}
              day(s)
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully updated
            </Alert>
          )}

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}
          >
            <Button onClick={onClose} variant="outlined" disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={success || isLoading}
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default EditRestDaysModal
